import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Award,
  Calendar,
  Camera,
  Eye,
  EyeOff,
  Lock,
  Palette,
  Play,
  RefreshCw,
  Settings,
  ShoppingCart,
  Swords,
  Trophy,
  Unlock,
  User,
  Users,
  Wand2,
  Wifi,
} from 'lucide-react';
import {
  FORMAT_PRESETS,
  type ClientGameView,
  type FormatId,
  type GameIntent,
  type ImportedDeckPayload,
  type PublicCardView,
  type SessionType,
} from '#shared';
import { soundEngine } from './audio/soundEngine.ts';
import { StreamedCalendarModal } from './components/CalendarAndStreamingModal.tsx';
import { CosmeticsGalleryModal } from './components/CosmeticsGalleryModal.tsx';
import { DeckCartModal } from './components/DeckCartModal.tsx';
import { PaperWebcamModal } from './components/PaperWebcamModal.tsx';
import { FanHand } from './components/FanHand.tsx';
import { FlightLayer } from './components/FlightLayer.tsx';
import {
  FormatLeaderboardModal,
  PlayNowQueuePanel,
  PostGameReportModal,
  ReplayViewerModal,
} from './components/LobbyQueueReplayModals.tsx';
import {
  AuthAccountModal,
  CardInspectorModal,
  DeckLoaderModal,
  OpeningHandModal,
  PrivateLookModal,
  SetupAndLogModal,
  YourTurnPrompt,
  ZoneBrowserModal,
} from './components/Modals.tsx';
import { PhaseTrackerBar } from './components/PhaseTrackerBar.tsx';
import { PlayerBoard } from './components/PlayerBoard.tsx';
import {
  GrantedLibraryViewerModal,
  GrantLibraryAccessModal,
  PregameRuleZeroAndPodModal,
} from './components/PodAndPregameModals.tsx';
import {
  PlaytestHelpersAndCountersModal,
  type PlaytestHelperTab,
} from './components/PlaytestHelpersAndCountersModal.tsx';
import { TableSidebar } from './components/TableSidebar.tsx';
import {
  applyOptimisticIntentToView,
  getPlaytableWsBaseUrl,
  isLocalSandboxMode,
  playtableApi,
  setCsrfToken,
  type AccountProfileDTO,
} from './lib/clientApi.ts';
import {
  dispatchLocalHostAction,
  dispatchLocalSandboxIntent,
  getLocalProjectedView,
  subscribeLocalTableUpdates,
} from './lib/localSandboxEngine.ts';

export interface PlaytableEmbedProps {
  embeddedInDeckstr?: boolean;
  deckstrDeck?: ImportedDeckPayload | null;
  deckstrUserHandle?: string | null;
  autoGoldfishOnMount?: boolean;
  onSwitchToLocalCouch?: () => void;
}

export function App({
  embeddedInDeckstr = false,
  deckstrDeck = null,
  deckstrUserHandle = null,
  autoGoldfishOnMount = false,
  onSwitchToLocalCouch,
}: PlaytableEmbedProps = {}) {
  const [serverStatus, setServerStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [devAuthEnabled, setDevAuthEnabled] = useState(true);
  const [profile, setProfile] = useState<AccountProfileDTO | null>(null);

  // Active table connection state
  const [activeTableId, setActiveTableId] = useState<string | null>(null);
  const [overlaySecret, setOverlaySecret] = useState<string | null>(null);
  const [sessionMode, setSessionMode] = useState<SessionType>('table');
  const [gameView, setGameView] = useState<ClientGameView | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [pingMs, setPingMs] = useState<number | null>(null);
  const [toastError, setToastError] = useState<string | null>(null);

  // Lobby create/join form state (matches reference Create Game design)
  const [createVisibility, setCreateVisibility] = useState<'private' | 'public'>('private');
  const [createName, setCreateName] = useState("Friday Night Pod");
  const [createFormat, setCreateFormat] = useState<FormatId>('commander');
  const [createMaxSeats, setCreateMaxSeats] = useState<number>(4);
  const [createBracket, setCreateBracket] = useState<'' | '1' | '2' | '3' | '4' | '5'>('');
  const [createLanguage, setCreateLanguage] = useState('English');
  const [createDescription, setCreateDescription] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createSpectators, setCreateSpectators] = useState(true);
  const [createSpectatorChat, setCreateSpectatorChat] = useState(true);
  const [createLateJoin, setCreateLateJoin] = useState(true);
  const [createCompetitive, setCreateCompetitive] = useState(false);
  const [inviteCodeInput, setInviteCodeInput] = useState('');
  const [joinPasswordInput, setJoinPasswordInput] = useState('');
  const [lobbyFilterFormat, setLobbyFilterFormat] = useState<FormatId | ''>('');
  const [lobbyFilterOpenOnly, setLobbyFilterOpenOnly] = useState(false);
  const [lobbyFilterSearch, setLobbyFilterSearch] = useState('');
  const [publicTables, setPublicTables] = useState<
    Awaited<ReturnType<typeof playtableApi.listPublicTables>>['tables']
  >([]);
  const [resumableGames, setResumableGames] = useState<
    Awaited<ReturnType<typeof playtableApi.listMyGames>>['resumable']
  >([]);
  const [leaderboardModalOpen, setLeaderboardModalOpen] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [cartModalDeck, setCartModalDeck] = useState<ImportedDeckPayload | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [replayTableId, setReplayTableId] = useState<string | null>(null);

  // UI view preferences & modals
  const [focusedOpponentSeatId, setFocusedOpponentSeatId] = useState<string | null>(null);
  const [followActivePlayer, setFollowActivePlayer] = useState(true);
  const [sidebarSide, setSidebarSide] = useState<'left' | 'right'>('right');
  const [desktopViewMode, setDesktopViewMode] = useState<'follow' | 'grid'>('follow');
  const [reducedMotion, setReducedMotion] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const [deckModalOpen, setDeckModalOpen] = useState(false);
  const [cosmeticsModalOpen, setCosmeticsModalOpen] = useState(false);
  const [paperModalOpen, setPaperModalOpen] = useState(false);
  const [paperStillsBySeat, setPaperStillsBySeat] = useState<Record<string, string>>({});
  const [podModalOpen, setPodModalOpen] = useState(false);
  const [grantLibModalOpen, setGrantLibModalOpen] = useState(false);
  const [helpersModalTab, setHelpersModalTab] = useState<PlaytestHelperTab | null>(null);
  const [openingHandDismissed, setOpeningHandDismissed] = useState(false);
  const [showEveryTurnPrompt, setShowEveryTurnPrompt] = useState(true);
  const [turnPromptOpen, setTurnPromptOpen] = useState(false);
  const lastPromptedTurnKeyRef = useRef<string>('');
  const [inspectedCard, setInspectedCard] = useState<PublicCardView | null>(null);
  const [browsingZone, setBrowsingZone] = useState<{
    seatId: string;
    zone: 'graveyard' | 'exile' | 'sideboard';
  } | null>(null);
  const [sharedDeckPreview, setSharedDeckPreview] = useState<
    Awaited<ReturnType<typeof playtableApi.getSharedDeck>>['sharedDeck'] | null
  >(null);
  const [sharedDeckCopiedMsg, setSharedDeckCopiedMsg] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const pairedWsTicketRef = useRef<string | null>(null);
  const clientSeqRef = useRef(1);

  const bootstrap = useCallback(async () => {
    try {
      const health = await playtableApi.checkHealth();
      setDevAuthEnabled(health.devAuthEnabled);
      setServerStatus('online');

      const me = await playtableApi.getMe();
      setCsrfToken(me.csrfToken);
      setProfile(me.profile);
      if (me.authenticated) {
        playtableApi
          .listMyGames()
          .then((mine) => setResumableGames(mine.resumable))
          .catch(() => {});
      }

      const pub = await playtableApi.listPublicTables();
      setPublicTables(pub.tables);

      // Check URL query params for OBS Overlay, Private Hand Dock, Companion QR Pair, or Invite Link
      const params = new URLSearchParams(window.location.search);
      const urlTableId = params.get('tableId');
      const urlOverlaySecret = params.get('overlaySecret');
      const urlSession = params.get('session');
      const urlPair = params.get('pair');
      const urlInvite = params.get('invite');
      const urlSpectate = params.get('spectate') === '1';
      const urlShareDeck = params.get('shareDeck');
      const urlAudio = params.get('audio');

      if (urlShareDeck) {
        try {
          const shared = await playtableApi.getSharedDeck(urlShareDeck);
          setSharedDeckPreview(shared.sharedDeck);
        } catch {
          setToastError('Shared deck link not found or no longer public.');
        }
      }

      if (urlPair) {
        try {
          const claimed = await playtableApi.claimCompanionPairCode(urlPair);
          pairedWsTicketRef.current = claimed.wsTicket;
          setSessionMode(claimed.sessionType);
          setActiveTableId(claimed.tableId);
        } catch {
          setToastError('Companion pairing code expired or already claimed.');
        }
      } else if (urlTableId && urlOverlaySecret) {
        document.body.style.background = 'transparent';
        if (urlAudio !== '1') {
          soundEngine.updatePrefs({ muted: true });
        }
        setSessionMode('overlay');
        setOverlaySecret(urlOverlaySecret);
        setActiveTableId(urlTableId);
      } else if (urlTableId && urlSession === 'private') {
        setSessionMode('private');
        setActiveTableId(urlTableId);
      } else if (urlInvite) {
        setInviteCodeInput(urlInvite.toUpperCase());
        if (urlSpectate) {
          const joined = await playtableApi.joinTable({
            inviteCode: urlInvite,
            asSpectator: true,
          });
          setSessionMode('spectator');
          setActiveTableId(joined.tableId);
        }
      }
    } catch {
      setServerStatus('offline');
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // Connect & auto-reconnect WebSocket whenever `activeTableId` is set
  useEffect(() => {
    if (!activeTableId || serverStatus !== 'online') return;

    if (isLocalSandboxMode()) {
      setWsConnected(true);
      setPingMs(1);
      const syncView = () => {
        const projected = getLocalProjectedView(activeTableId, sessionMode, overlaySecret);
        if (projected) setGameView(projected);
      };
      syncView();
      const unsub = subscribeLocalTableUpdates(activeTableId, syncView);
      return () => {
        unsub();
        setWsConnected(false);
      };
    }

    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let pingInterval: ReturnType<typeof setInterval> | null = null;

      const connectSocket = async () => {
      try {
        const wsBase = getPlaytableWsBaseUrl();
        let wsUrl = '';
        if (sessionMode === 'overlay' && overlaySecret) {
          wsUrl = `${wsBase}?tableId=${encodeURIComponent(
            activeTableId
          )}&overlaySecret=${encodeURIComponent(overlaySecret)}`;
        } else if (pairedWsTicketRef.current) {
          const pairedTicket = pairedWsTicketRef.current;
          pairedWsTicketRef.current = null;
          wsUrl = `${wsBase}?tableId=${encodeURIComponent(
            activeTableId
          )}&ticket=${encodeURIComponent(pairedTicket)}`;
        } else {
          const minted = await playtableApi.mintWsTicket(activeTableId, sessionMode);
          wsUrl = `${wsBase}?tableId=${encodeURIComponent(
            activeTableId
          )}&ticket=${encodeURIComponent(minted.wsTicket)}`;
        }
        if (cancelled) return;

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          setWsConnected(true);
          ws.send(JSON.stringify({ type: 'PING', clientTime: Date.now() }));
          pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'PING', clientTime: Date.now() }));
            }
          }, 8000);
        };

        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(String(ev.data));
            if (msg.type === 'GAME_VIEW') {
              setGameView(msg.view as ClientGameView);
            } else if (msg.type === 'PONG' && typeof msg.clientTime === 'number') {
              setPingMs(Math.max(1, Date.now() - msg.clientTime));
            } else if (msg.type === 'INTENT_ACK' && !msg.ok && msg.error) {
              setToastError(msg.error);
              setTimeout(() => setToastError(null), 3500);
            } else if (msg.type === 'WEBRTC_SIGNAL') {
              if (
                msg.signalKind === 'still-frame' &&
                typeof msg.fromSeatId === 'string' &&
                typeof msg.data?.jpegDataUrl === 'string'
              ) {
                setPaperStillsBySeat((prev) => ({
                  ...prev,
                  [msg.fromSeatId]: msg.data.jpegDataUrl,
                }));
              }
            } else if (msg.type === 'ERROR' && msg.error) {
              setToastError(msg.error);
              setTimeout(() => setToastError(null), 3500);
            }
          } catch {
            // ignore malformed
          }
        };

        ws.onclose = (ev) => {
          setWsConnected(false);
          if (pingInterval) clearInterval(pingInterval);
          if (!cancelled && ev.code !== 4003 && ev.code !== 4001) {
            reconnectTimer = setTimeout(connectSocket, 1400);
          }
        };
      } catch {
        if (!cancelled) {
          reconnectTimer = setTimeout(connectSocket, 2000);
        }
      }
    };

    connectSocket();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingInterval) clearInterval(pingInterval);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [activeTableId, sessionMode, overlaySecret, serverStatus]);

  const dispatchIntent = useCallback(
    (intent: GameIntent) => {
      if (isLocalSandboxMode()) {
        if (!activeTableId) return;
        const res = dispatchLocalSandboxIntent(activeTableId, intent);
        if (!res.ok && res.error) {
          setToastError(res.error);
          setTimeout(() => setToastError(null), 3500);
        } else if (res.view) {
          setGameView(res.view);
        }
        return;
      }

      setGameView((prev) => (prev ? applyOptimisticIntentToView(prev, intent) : prev));
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        setToastError('Reconnecting to table...');
        return;
      }
      const clientSeq = clientSeqRef.current++;
      ws.send(JSON.stringify({ type: 'SUBMIT_INTENT', clientSeq, intent }));
    },
    [activeTableId]
  );

  const sendHostAction = useCallback(
    (action: Record<string, unknown>) => {
      if (isLocalSandboxMode()) {
        if (activeTableId) dispatchLocalHostAction(activeTableId, action);
        return;
      }
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: 'HOST_ACTION', action }));
    },
    [activeTableId]
  );

  const sendWebRtcSignal = useCallback(
    (signalKind: string, data: unknown, targetSeatId?: string) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: 'WEBRTC_SIGNAL', signalKind, data, targetSeatId }));
    },
    []
  );

  // Follow active opponent automatically when enabled & trigger "Your turn" prompt
  useEffect(() => {
    if (!gameView) return;
    if (followActivePlayer && gameView.activeSeatId && gameView.activeSeatId !== gameView.viewerSeatId) {
      setFocusedOpponentSeatId(gameView.activeSeatId);
    }
    if (
      gameView.status === 'active' &&
      gameView.viewerSeatId &&
      gameView.activeSeatId === gameView.viewerSeatId &&
      gameView.phase === 'untap' &&
      gameView.turnNumber >= 2 &&
      showEveryTurnPrompt
    ) {
      const turnKey = `${gameView.tableId}:${gameView.turnNumber}:${gameView.activeSeatId}`;
      if (lastPromptedTurnKeyRef.current !== turnKey) {
        lastPromptedTurnKeyRef.current = turnKey;
        setTurnPromptOpen(true);
      }
    }
    if (gameView.turnNumber <= 1 && gameView.viewerSeatId && !gameView.seats[gameView.viewerSeatId]?.handConfirmed) {
      // keep opening hand review available on fresh match
    } else if (gameView.viewerSeatId && gameView.seats[gameView.viewerSeatId]?.handConfirmed) {
      setOpeningHandDismissed(false);
    }
  }, [
    gameView?.tableId,
    gameView?.status,
    gameView?.turnNumber,
    gameView?.phase,
    gameView?.activeSeatId,
    gameView?.viewerSeatId,
    followActivePlayer,
    showEveryTurnPrompt,
  ]);

  // Global tabletop keyboard controls (skipped when typing inside inputs/textareas)
  useEffect(() => {
    if (!gameView || sessionMode === 'overlay') return;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      const mySid = gameView.viewerSeatId;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        dispatchIntent({ type: 'UNDO_LAST_ACTION' });
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'd':
          if (mySid) {
            e.preventDefault();
            dispatchIntent({ type: 'DRAW_CARDS', count: 1 });
          }
          break;
        case 'u':
          if (mySid) {
            e.preventDefault();
            dispatchIntent({ type: 'UNTAP_ALL' });
          }
          break;
        case 'r':
        case 's':
          if (mySid && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            dispatchIntent({ type: 'SHUFFLE_LIBRARY' });
          }
          break;
        case 'v':
          if (mySid && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            dispatchIntent({ type: 'SEARCH_LIBRARY_START' });
          }
          break;
        case 'n':
          if (mySid && gameView.activeSeatId === mySid && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            dispatchIntent({ type: 'PASS_TURN' });
          }
          break;
        case 'e':
          if (mySid && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            setTurnPromptOpen((v) => !v);
          }
          break;
        case ' ':
          if (mySid && gameView.activeSeatId === mySid) {
            e.preventDefault();
            dispatchIntent({ type: 'ADVANCE_PHASE' });
          }
          break;
        case 'w':
          if (mySid && gameView.activeSeatId !== mySid) {
            e.preventDefault();
            const existing = gameView.flags.find((f) => f.seatId === mySid);
            if (existing) dispatchIntent({ type: 'CLEAR_FLAG', flagId: existing.flagId });
            else dispatchIntent({ type: 'RAISE_FLAG', reason: 'respond' });
          }
          break;
        case 'm': {
          e.preventDefault();
          const prefs = soundEngine.getPrefs();
          soundEngine.updatePrefs({ muted: !prefs.muted });
          break;
        }
        case 'escape':
          setInspectedCard(null);
          setBrowsingZone(null);
          setDeckModalOpen(false);
          setCosmeticsModalOpen(false);
          setCartModalOpen(false);
          setPaperModalOpen(false);
          setPodModalOpen(false);
          setSetupModalOpen(false);
          setGrantLibModalOpen(false);
          setHelpersModalTab(null);
          setReportModalOpen(false);
          break;
        case 'arrowup':
          if (mySid) {
            e.preventDefault();
            dispatchIntent({ type: 'ADJUST_LIFE', delta: e.shiftKey ? 10 : 1 });
          }
          break;
        case 'arrowdown':
          if (mySid) {
            e.preventDefault();
            dispatchIntent({ type: 'ADJUST_LIFE', delta: e.shiftKey ? -10 : -1 });
          }
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [gameView, sessionMode, dispatchIntent]);

  const ensureSignedInQuick = async (): Promise<AccountProfileDTO | null> => {
    if (profile) return profile;
    if (devAuthEnabled) {
      const cleanDeckstrHandle = deckstrUserHandle?.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 22);
      const randomSuffix = Math.floor(100 + Math.random() * 900);
      const res = await playtableApi.devLogin({
        provider: 'deckstr',
        handle: cleanDeckstrHandle || `Mage${randomSuffix}`,
        mode: 'signin',
      });
      setCsrfToken(res.csrfToken);
      setProfile(res.profile);
      return res.profile;
    }
    setAuthModalOpen(true);
    return null;
  };

  const handleStartGoldfishWithDeck = async (customDeck?: ImportedDeckPayload | null) => {
    try {
      const acct = await ensureSignedInQuick();
      if (!acct) return;
      const chosenDeck = customDeck ?? deckstrDeck ?? null;
      const chosenFormat = chosenDeck?.format ?? createFormat;
      const created = await playtableApi.createTable({
        format: chosenFormat,
        settings: {
          name: chosenDeck ? `${chosenDeck.name} (Goldfish)` : `${acct.displayName}'s Goldfish`,
          maxSeats: 1,
          isPublic: false,
          isTracked: false,
        },
        quickStartGoldfish: true,
        initialDeck: chosenDeck,
      });
      setOverlaySecret(created.overlaySecret);
      setSessionMode('table');
      setActiveTableId(created.tableId);
    } catch (err: any) {
      setToastError(err.message);
    }
  };

  const handleStartGoldfish = () => handleStartGoldfishWithDeck(deckstrDeck);

  const autoGoldfishedRef = useRef(false);
  useEffect(() => {
    if (autoGoldfishOnMount && serverStatus === 'online' && deckstrDeck && !autoGoldfishedRef.current) {
      autoGoldfishedRef.current = true;
      handleStartGoldfishWithDeck(deckstrDeck);
    }
  }, [autoGoldfishOnMount, serverStatus, deckstrDeck]);

  const handleCreateTable = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const acct = await ensureSignedInQuick();
      if (!acct) return;
      const created = await playtableApi.createTable({
        format: createFormat,
        password: createPassword.trim() || undefined,
        settings: {
          name: createName.trim() || 'Playtable Pod',
          maxSeats: createMaxSeats,
          isPublic: createVisibility === 'public',
          bracket: createBracket ? (Number(createBracket) as 1 | 2 | 3 | 4 | 5) : null,
          language: createLanguage,
          description: createDescription.trim(),
          spectatorsAllowed: createSpectators,
          allowSpectatorChat: createSpectatorChat,
          allowLateJoinRequests: createLateJoin,
          competitiveMode: createCompetitive,
        },
        quickStartGoldfish: createMaxSeats === 1,
        initialDeck: deckstrDeck,
      });
      setOverlaySecret(created.overlaySecret);
      setSessionMode('table');
      setActiveTableId(created.tableId);
    } catch (err: any) {
      setToastError(err.message);
    }
  };

  const handleJoinByCode = async (asSpectator: boolean) => {
    try {
      if (!asSpectator) {
        const acct = await ensureSignedInQuick();
        if (!acct) return;
      }
      const res = await playtableApi.joinTable({
        inviteCode: inviteCodeInput.trim().toUpperCase(),
        password: joinPasswordInput.trim() || undefined,
        asSpectator,
      });
      setSessionMode(res.sessionType);
      setActiveTableId(res.tableId);
    } catch (err: any) {
      setToastError(err.message);
    }
  };

  // ==========================================================================
  // SERVER-DOWN STATUS SCREEN (Never shows a fake login/username prompt!)
  // ==========================================================================
  if (serverStatus === 'offline') {
    return (
      <main className="min-h-screen bg-[#070a12] text-slate-100 flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl bg-slate-950 border border-rose-500/40 p-6 text-center space-y-4 shadow-2xl">
          <div className="w-12 h-12 rounded-2xl bg-rose-500/15 border border-rose-500/40 flex items-center justify-center mx-auto text-rose-400">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h1 className="text-lg font-extrabold">Playtable Server Unreachable</h1>
          <p className="text-xs text-slate-400 leading-relaxed">
            The authoritative game server is currently offline or restarting. Your account and in-progress event logs are safe—no re-login is needed once connectivity returns.
          </p>
          <button
            type="button"
            onClick={bootstrap}
            className="w-full py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Retry Connection</span>
          </button>
        </div>
      </main>
    );
  }

  // ==========================================================================
  // OBS TRANSPARENT DISPLAY OVERLAY SURFACE (`?overlaySecret=...`)
  // ==========================================================================
  if (sessionMode === 'overlay' && gameView) {
    const visibleSeats = gameView.activeSeatOrder.map((id) => gameView.seats[id]).filter(Boolean);
    const overlayParams =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const isVerticalLayout = overlayParams?.get('layout') === 'vertical';
    const isFramingPreview = overlayParams?.get('preview') === '1';
    const delayedHandSeat = visibleSeats.find(
      (s) => Array.isArray(s.zones.handCards) && s.zones.handCards.length > 0
    );

    return (
      <div
        className={`min-h-screen bg-transparent p-4 flex flex-col justify-between gap-3 pointer-events-none ${
          isVerticalLayout ? 'max-w-[540px] mx-auto' : ''
        } ${isFramingPreview ? 'outline outline-2 outline-dashed outline-amber-400/80' : ''}`}
      >
        <FlightLayer recentLog={gameView.recentLog} reducedMotion={reducedMotion} />
        {isFramingPreview && (
          <div className="px-3 py-1 rounded-lg bg-amber-500/90 text-slate-950 font-black text-[11px] text-center uppercase tracking-wider">
            OBS Framing Safe-Zone Preview ({isVerticalLayout ? '1080×1920 Vertical' : '1920×1080 Landscape'})
          </div>
        )}
        <div className="flex items-center justify-between px-3 py-1.5 rounded-xl bg-slate-950/85 border border-slate-700/80 text-xs">
          <span className="font-extrabold text-amber-300">
            Playtable Live · Turn {gameView.turnNumber} ({gameView.phase.toUpperCase()})
          </span>
          <span className="text-slate-300">
            Active: {gameView.seats[gameView.activeSeatId]?.displayName}
            {gameView.settings.spectatorDelaySec > 0
              ? ` · ${gameView.settings.spectatorDelaySec}s Broadcast Delay`
              : ''}
          </span>
        </div>
        <div className={`grid gap-3 ${isVerticalLayout ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
          {visibleSeats.map((s) => (
            <PlayerBoard
              key={s.seatId}
              seat={s}
              isMe={false}
              isActiveTurn={gameView.activeSeatId === s.seatId}
              seatFlag={gameView.flags.find((f) => f.seatId === s.seatId)}
              useCommandZone={gameView.settings.useCommandZone}
              compact
              reducedMotion={reducedMotion}
              onDispatch={() => {}}
              onInspectCard={() => {}}
              onOpenCardMenu={() => {}}
              onOpenZoneBrowser={() => {}}
            />
          ))}
        </div>
        {delayedHandSeat && delayedHandSeat.zones.handCards && (
          <div className="px-3 py-2 rounded-xl bg-slate-950/90 border border-emerald-500/40 space-y-1">
            <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider">
              {delayedHandSeat.displayName} — Delayed Hand ({gameView.settings.spectatorDelaySec}s delay)
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto">
              {delayedHandSeat.zones.handCards.map((c) => (
                <div
                  key={c.instanceId}
                  className="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-[10px] text-white font-semibold shrink-0"
                >
                  {c.name}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==========================================================================
  // PRIVATE HAND / OBS STREAMER CONTROL DOCK (`?session=private`)
  // ==========================================================================
  if (sessionMode === 'private' && gameView && gameView.viewerSeatId) {
    const mySeat = gameView.seats[gameView.viewerSeatId];
    return (
      <main className="min-h-screen bg-[#070a12] text-slate-100 p-3 flex flex-col justify-between gap-3">
        <header className="flex items-center justify-between px-3 py-2 rounded-xl bg-slate-950 border border-indigo-500/40 text-xs">
          <span className="font-bold text-indigo-300">
            Private Hand & OBS Control Dock · {mySeat.displayName}
          </span>
          <span className="text-slate-400">Never broadcast this window</span>
        </header>

        <PhaseTrackerBar view={gameView} onDispatch={dispatchIntent} />

        <FanHand
          seatId={mySeat.seatId}
          handCount={mySeat.zones.handCount}
          handCards={mySeat.zones.handCards}
          sleeveUrl={mySeat.sleeveUrl}
          streamMode={false}
          isMyTurnMain={
            gameView.activeSeatId === mySeat.seatId &&
            (gameView.phase === 'main1' || gameView.phase === 'main2')
          }
          reducedMotion={reducedMotion}
          onPlayCard={(c) =>
            dispatchIntent({ type: 'MOVE_CARD', instanceId: c.instanceId, toZone: 'battlefield' })
          }
          onInspectCard={(c) => setInspectedCard(c)}
          onOpenCardMenu={(c) => setInspectedCard(c)}
        />

        {mySeat.activePrivateAction && mySeat.privateLookCards && (
          <PrivateLookModal
            kind={mySeat.activePrivateAction.kind}
            cards={mySeat.privateLookCards}
            onDispatch={dispatchIntent}
          />
        )}
      </main>
    );
  }

  // ==========================================================================
  // ACTIVE TABLETOP VIEW (PORTRAIT MOBILE + DESKTOP)
  // ==========================================================================
  if (activeTableId && gameView) {
    const mySeat = gameView.viewerSeatId ? gameView.seats[gameView.viewerSeatId] : null;
    const otherSeatIds = gameView.activeSeatOrder.filter((id) => id !== gameView.viewerSeatId);
    const currentFocusedId =
      focusedOpponentSeatId && otherSeatIds.includes(focusedOpponentSeatId)
        ? focusedOpponentSeatId
        : otherSeatIds[0] ?? null;
    const focusedOpponent = currentFocusedId ? gameView.seats[currentFocusedId] : null;
    const browsingSeat = browsingZone ? gameView.seats[browsingZone.seatId] : null;

    return (
      <div
        className={`${
          embeddedInDeckstr ? 'fixed inset-0 z-[70]' : 'h-screen w-screen'
        } bg-[#070a12] text-slate-100 flex overflow-hidden`}
      >
        {/* Screen-Reader Live Region for WCAG AA Turn, Phase & Priority Flag Announcements */}
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {`Turn ${gameView.turnNumber}, ${gameView.phase} phase. Active player: ${
            gameView.seats[gameView.activeSeatId]?.displayName ?? gameView.activeSeatId
          }. ${
            gameView.flags.length > 0
              ? `${gameView.flags.length} interaction flag open.`
              : 'No open interaction flags.'
          }`}
        </div>
        <FlightLayer recentLog={gameView.recentLog} reducedMotion={reducedMotion} />

        {sidebarSide === 'left' && (
          <TableSidebar
            view={gameView}
            overlaySecret={overlaySecret}
            onOverlaySecretChanged={setOverlaySecret}
            side={sidebarSide}
            onChangeSide={setSidebarSide}
            viewMode={desktopViewMode}
            onChangeViewMode={setDesktopViewMode}
            reducedMotion={reducedMotion}
            onToggleReducedMotion={setReducedMotion}
            onDispatch={dispatchIntent}
            onHostAction={sendHostAction}
            onOpenDeckLoader={() => setDeckModalOpen(true)}
            onOpenCosmetics={() => setCosmeticsModalOpen(true)}
            onOpenCartOptimizer={() => setCartModalOpen(true)}
            onOpenReportModal={() => setReportModalOpen(true)}
            onOpenReplayModal={() => setReplayTableId(gameView.tableId)}
            onInspectCard={(c) => setInspectedCard(c)}
            onLeaveTable={() => {
              setActiveTableId(null);
              setGameView(null);
            }}
          />
        )}

        {/* Main Tabletop Column */}
        <div className="flex-1 flex flex-col justify-between p-2 sm:p-3 gap-2 overflow-y-auto min-w-0">
          {/* Compact Top Bar */}
          <header className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-xl bg-slate-950/90 border border-slate-800/90 text-xs shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={() => {
                  setActiveTableId(null);
                  setGameView(null);
                }}
                className="font-black tracking-tight text-amber-400 hover:text-amber-300"
              >
                PLAYTABLE
              </button>
              <span className="text-slate-600">·</span>
              <span className="font-mono font-bold text-slate-200">{gameView.inviteCode}</span>
              <span className="hidden sm:inline text-slate-400 truncate">
                ({FORMAT_PRESETS[gameView.settings.format]?.label ?? gameView.settings.format})
              </span>
              {gameView.viewerSessionType === 'spectator' && (
                <span className="px-2 py-0.5 rounded-full bg-sky-500/20 border border-sky-400/60 text-sky-200 text-[10px] font-bold flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  SPECTATOR
                </span>
              )}
            </div>

            {/* Opponent Overview Chips (Focus-Plus-Overview for 2..8 Players) */}
            {otherSeatIds.length > 1 && (
              <div className="flex items-center gap-1 overflow-x-auto">
                {otherSeatIds.map((sid) => {
                  const s = gameView.seats[sid];
                  if (!s) return null;
                  const focused = sid === currentFocusedId;
                  return (
                    <button
                      key={sid}
                      type="button"
                      onClick={() => {
                        setFollowActivePlayer(false);
                        setFocusedOpponentSeatId(sid);
                      }}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 border shrink-0 ${
                        focused
                          ? 'bg-indigo-600/35 border-indigo-400 text-white'
                          : 'bg-slate-900 border-slate-800 text-slate-300'
                      }`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.profileColor }} />
                      <span>{s.displayName}</span>
                      <span className="text-amber-300">{s.life}♥</span>
                      <span className="text-slate-400">{s.zones.handCount}✋</span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex items-center gap-1.5 shrink-0">
              {/* Latency & WS Indicator */}
              <span
                className={`px-2 py-0.5 rounded bg-slate-900 border text-[10px] font-mono flex items-center gap-1 ${
                  wsConnected ? 'border-slate-800 text-emerald-400' : 'border-rose-500/60 text-rose-300'
                }`}
              >
                <Wifi className="w-3 h-3" />
                <span>{wsConnected ? `${pingMs ?? 1}ms` : 'reconnecting'}</span>
              </span>

              {/* One-Tap Stream Panic Hide */}
              {mySeat && (
                <button
                  type="button"
                  onClick={() =>
                    dispatchIntent({ type: 'TOGGLE_STREAM_MODE', enabled: !mySeat.streamMode })
                  }
                  title="Panic Stream-Hide Toggle"
                  className={`px-2 py-1 rounded-lg border text-[10px] font-bold flex items-center gap-1 ${
                    mySeat.streamMode
                      ? 'bg-rose-600 border-rose-400 text-white'
                      : 'bg-slate-900 border-slate-700 text-slate-300 hover:text-white'
                  }`}
                >
                  <EyeOff className="w-3 h-3" />
                  <span className="hidden sm:inline">{mySeat.streamMode ? 'Hidden' : 'Stream'}</span>
                </button>
              )}

              {gameView.status === 'lobby' && (
                <button
                  type="button"
                  onClick={() => dispatchIntent({ type: 'START_MATCH', autoShuffle: true })}
                  className="px-2.5 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-[11px]"
                >
                  Start Match
                </button>
              )}

              {gameView.status === 'finished' && (gameView.isHost || gameView.isJudge) && (
                <button
                  type="button"
                  onClick={() => dispatchIntent({ type: 'REMATCH_POD', reshuffleSeats: true })}
                  className="px-2.5 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-[11px]"
                >
                  Rematch Pod
                </button>
              )}

              {gameView.status === 'pregame' && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await playtableApi.declineMatchedPod(gameView.tableId);
                      setActiveTableId(null);
                      setGameView(null);
                    } catch (err: any) {
                      setToastError(err.message);
                    }
                  }}
                  className="px-2 py-1 rounded-lg bg-rose-950/90 hover:bg-rose-800 border border-rose-500/60 text-rose-200 font-bold text-[10px]"
                  title="Decline matched pod within 60s with zero rating penalty"
                >
                  Rule 0 Decline (0 Penalty)
                </button>
              )}

              {gameView.viewerSessionType === 'spectator' &&
                (gameView.status === 'active' || gameView.status === 'paused') &&
                gameView.settings.allowLateJoinRequests !== false && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await ensureSignedInQuick();
                        await playtableApi.requestLateJoin(gameView.tableId, joinPasswordInput || undefined);
                        setToastError('Late join request sent to table host!');
                        setTimeout(() => setToastError(null), 3000);
                      } catch (err: any) {
                        setToastError(err.message);
                      }
                    }}
                    className="px-2 py-1 rounded-lg bg-emerald-600/30 hover:bg-emerald-600/50 border border-emerald-400/50 text-emerald-100 font-bold text-[10px]"
                  >
                    Request Open Seat
                  </button>
                )}

              <button
                type="button"
                onClick={() => setReportModalOpen(true)}
                title="Two-Tap Match Outcome Report & Scrubbable Replay"
                className="px-2 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-emerald-500/40 text-[10px] font-bold text-emerald-200 flex items-center gap-1"
              >
                <Trophy className="w-3 h-3 text-emerald-400" />
                <span className="hidden sm:inline">Report / Replay</span>
              </button>

              <button
                type="button"
                onClick={() => setHelpersModalTab('tokens')}
                title="Tokens, Commander Damage Matrix, Mana Pool, Designations, Cascade & Notes"
                className="px-2 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-amber-500/40 text-[10px] font-bold text-amber-200 flex items-center gap-1"
              >
                <Wand2 className="w-3 h-3 text-amber-400" />
                <span className="hidden sm:inline">Helpers / Cmdr</span>
                {(gameView.designations.monarchSeatId ||
                  gameView.designations.initiativeSeatId ||
                  gameView.designations.dayNight) && (
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                )}
              </button>

              {mySeat && (
                <button
                  type="button"
                  onClick={() => setPaperModalOpen(true)}
                  title="Paper & Webcam Hybrid Mode (Deck-Scoped Card Scanner & Digital Twin)"
                  className={`px-2 py-1 rounded-lg border text-[10px] font-bold flex items-center gap-1 ${
                    mySeat.paperMode?.enabled
                      ? 'bg-emerald-600/30 border-emerald-400 text-emerald-100'
                      : 'bg-slate-900 hover:bg-slate-800 border-emerald-500/35 text-emerald-200'
                  }`}
                >
                  <Camera className="w-3 h-3 text-emerald-400" />
                  <span className="hidden sm:inline">Paper Cam</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => setCosmeticsModalOpen(true)}
                title="Deck-Identity Card-Art Playmat & Sleeves Gallery"
                className="px-2 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-amber-500/35 text-[10px] font-bold text-amber-200 flex items-center gap-1"
              >
                <Palette className="w-3 h-3 text-amber-400" />
                <span className="hidden sm:inline">Mat / Sleeves</span>
              </button>

              <button
                type="button"
                onClick={() => setPodModalOpen(true)}
                title="Rule 0, Magical Chairs, Teams (2HG) & Pod Controls"
                className="px-2 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 text-[10px] font-bold text-slate-200 flex items-center gap-1"
              >
                <Users className="w-3 h-3 text-violet-400" />
                <span className="hidden sm:inline">Pod / Rule 0</span>
              </button>

              <button
                type="button"
                onClick={() => setSetupModalOpen(true)}
                aria-label="Table setup & logs"
                className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200"
              >
                <Settings className="w-3.5 h-3.5" />
              </button>
            </div>
          </header>

          {toastError && (
            <div className="px-3 py-1.5 rounded-xl bg-rose-950/90 border border-rose-500/60 text-rose-200 text-xs text-center">
              {toastError}
            </div>
          )}

          {/* Boards Area: Multi-Board Desktop Grid OR Ultra PRO 2-Mat Mirrored Focus Stack */}
          {desktopViewMode === 'grid' && otherSeatIds.length >= 2 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 flex-1">
              {gameView.activeSeatOrder.map((sid) => {
                const s = gameView.seats[sid];
                if (!s) return null;
                const mine = sid === gameView.viewerSeatId;
                return (
                  <PlayerBoard
                    key={sid}
                    seat={s}
                    isMe={mine}
                    mirrored={!mine}
                    isActiveTurn={gameView.activeSeatId === sid}
                    seatFlag={gameView.flags.find((f) => f.seatId === sid)}
                    useCommandZone={gameView.settings.useCommandZone}
                    compact
                    reducedMotion={reducedMotion}
                    onDispatch={dispatchIntent}
                    onInspectCard={(c) => setInspectedCard(c)}
                    onOpenCardMenu={(c) => setInspectedCard(c)}
                    onOpenZoneBrowser={(seatId, zone) => setBrowsingZone({ seatId, zone })}
                    onOpenDeckLoader={mine ? () => setDeckModalOpen(true) : undefined}
                    onOpenCosmetics={mine ? () => setCosmeticsModalOpen(true) : undefined}
                    onOpenPaperMode={mine ? () => setPaperModalOpen(true) : undefined}
                    lowBandwidthStillUrl={paperStillsBySeat[sid] ?? null}
                    onOpenGrantLibraryAccess={mine ? () => setGrantLibModalOpen(true) : undefined}
                    onOpenHelpers={mine ? (t) => setHelpersModalTab(t) : undefined}
                  />
                );
              })}
            </div>
          ) : (
            <div className="flex-1 flex flex-col justify-between gap-2">
              {focusedOpponent && (
                <PlayerBoard
                  seat={focusedOpponent}
                  isMe={false}
                  mirrored={true}
                  isActiveTurn={gameView.activeSeatId === focusedOpponent.seatId}
                  seatFlag={gameView.flags.find((f) => f.seatId === focusedOpponent.seatId)}
                  useCommandZone={gameView.settings.useCommandZone}
                  compact={otherSeatIds.length > 0}
                  reducedMotion={reducedMotion}
                  onDispatch={dispatchIntent}
                  onInspectCard={(c) => setInspectedCard(c)}
                  onOpenCardMenu={(c) => setInspectedCard(c)}
                  onOpenZoneBrowser={(seatId, zone) => setBrowsingZone({ seatId, zone })}
                  lowBandwidthStillUrl={paperStillsBySeat[focusedOpponent.seatId] ?? null}
                />
              )}

              {/* Center Ultra PRO Phase Strip & Arena Priority / Next Gate */}
              <PhaseTrackerBar view={gameView} onDispatch={dispatchIntent} />

              {mySeat && (
                <PlayerBoard
                  seat={mySeat}
                  isMe={true}
                  mirrored={false}
                  isActiveTurn={gameView.activeSeatId === mySeat.seatId}
                  seatFlag={gameView.flags.find((f) => f.seatId === mySeat.seatId)}
                  useCommandZone={gameView.settings.useCommandZone}
                  reducedMotion={reducedMotion}
                  onDispatch={dispatchIntent}
                  onInspectCard={(c) => setInspectedCard(c)}
                  onOpenCardMenu={(c) => setInspectedCard(c)}
                  onOpenZoneBrowser={(seatId, zone) => setBrowsingZone({ seatId, zone })}
                  onOpenDeckLoader={() => setDeckModalOpen(true)}
                  onOpenCosmetics={() => setCosmeticsModalOpen(true)}
                  onOpenPaperMode={() => setPaperModalOpen(true)}
                  lowBandwidthStillUrl={paperStillsBySeat[mySeat.seatId] ?? null}
                  onOpenGrantLibraryAccess={() => setGrantLibModalOpen(true)}
                  onOpenHelpers={(t) => setHelpersModalTab(t)}
                />
              )}
            </div>
          )}

          {/* My Asymmetrical Fanned Hand */}
          {mySeat && (
            <FanHand
              seatId={mySeat.seatId}
              handCount={mySeat.zones.handCount}
              handCards={mySeat.zones.handCards}
              sleeveUrl={mySeat.sleeveUrl}
              streamMode={mySeat.streamMode}
              isMyTurnMain={
                gameView.activeSeatId === mySeat.seatId &&
                (gameView.phase === 'main1' || gameView.phase === 'main2')
              }
              reducedMotion={reducedMotion}
              onPlayCard={(c) =>
                dispatchIntent({
                  type: 'MOVE_CARD',
                  instanceId: c.instanceId,
                  toZone: 'battlefield',
                })
              }
              onInspectCard={(c) => {
                dispatchIntent({ type: 'INSPECT_CARD', instanceId: c.instanceId });
                setInspectedCard(c);
              }}
              onOpenCardMenu={(c) => setInspectedCard(c)}
            />
          )}
        </div>

        {sidebarSide === 'right' && (
          <TableSidebar
            view={gameView}
            overlaySecret={overlaySecret}
            onOverlaySecretChanged={setOverlaySecret}
            side={sidebarSide}
            onChangeSide={setSidebarSide}
            viewMode={desktopViewMode}
            onChangeViewMode={setDesktopViewMode}
            reducedMotion={reducedMotion}
            onToggleReducedMotion={setReducedMotion}
            onDispatch={dispatchIntent}
            onHostAction={sendHostAction}
            onOpenDeckLoader={() => setDeckModalOpen(true)}
            onOpenCosmetics={() => setCosmeticsModalOpen(true)}
            onOpenCartOptimizer={() => setCartModalOpen(true)}
            onOpenReportModal={() => setReportModalOpen(true)}
            onOpenReplayModal={() => setReplayTableId(gameView.tableId)}
            onInspectCard={(c) => setInspectedCard(c)}
            onLeaveTable={() => {
              setActiveTableId(null);
              setGameView(null);
            }}
          />
        )}

        {/* Active Modals */}
        {mySeat?.activePrivateAction && mySeat.privateLookCards && (
          <PrivateLookModal
            kind={mySeat.activePrivateAction.kind}
            cards={mySeat.privateLookCards}
            onDispatch={dispatchIntent}
          />
        )}

        {inspectedCard && (
          <CardInspectorModal
            card={inspectedCard}
            canControl={
              Boolean(gameView.viewerSeatId && inspectedCard.controllerSeatId === gameView.viewerSeatId) ||
              gameView.isHost ||
              gameView.isJudge
            }
            battlefieldTargets={mySeat?.zones.battlefield ?? []}
            savedForLater={mySeat?.savedForLater}
            onDispatch={dispatchIntent}
            onClose={() => setInspectedCard(null)}
          />
        )}

        {browsingZone && browsingSeat && (
          <ZoneBrowserModal
            title={`${browsingSeat.displayName} — ${browsingZone.zone.toUpperCase()}`}
            zoneKind={browsingZone.zone}
            cards={
              browsingZone.zone === 'sideboard'
                ? browsingSeat.zones.sideboardCards ?? []
                : browsingSeat.zones[browsingZone.zone]
            }
            canMove={browsingZone.seatId === gameView.viewerSeatId}
            onDispatch={dispatchIntent}
            onInspect={(c) => setInspectedCard(c)}
            onClose={() => setBrowsingZone(null)}
          />
        )}

        {mySeat &&
          gameView.status === 'active' &&
          !mySeat.handConfirmed &&
          !mySeat.streamMode &&
          !openingHandDismissed &&
          mySeat.zones.handCards &&
          mySeat.zones.handCards.length > 0 && (
            <OpeningHandModal
              handCards={mySeat.zones.handCards}
              libraryCount={mySeat.zones.libraryCount}
              mulliganCount={mySeat.mulliganCount}
              freeFirstMulligan={gameView.settings.freeFirstMulligan}
              onDispatch={dispatchIntent}
              onInspect={(c) => setInspectedCard(c)}
              onClose={() => setOpeningHandDismissed(true)}
            />
          )}

        {mySeat && turnPromptOpen && gameView.activeSeatId === mySeat.seatId && (
          <YourTurnPrompt
            showEveryTurn={showEveryTurnPrompt}
            onToggleShowEveryTurn={setShowEveryTurnPrompt}
            onDispatch={dispatchIntent}
            onClose={() => setTurnPromptOpen(false)}
          />
        )}

        {deckModalOpen && (
          <DeckLoaderModal
            format={gameView.settings.format}
            mySeat={mySeat}
            onDispatch={dispatchIntent}
            onOpenCosmetics={() => setCosmeticsModalOpen(true)}
            onBuyDeck={(d) => {
              setCartModalDeck(d);
              setDeckModalOpen(false);
              setCartModalOpen(true);
            }}
            onClose={() => setDeckModalOpen(false)}
          />
        )}

        {cartModalOpen && (
          <DeckCartModal
            initialDeck={cartModalDeck}
            profile={profile}
            ensureSignedIn={ensureSignedInQuick}
            onClose={() => setCartModalOpen(false)}
          />
        )}

        {cosmeticsModalOpen && (
          <CosmeticsGalleryModal
            view={gameView}
            onDispatch={dispatchIntent}
            onOpenDeckLoader={() => setDeckModalOpen(true)}
            onClose={() => setCosmeticsModalOpen(false)}
          />
        )}

        {paperModalOpen && (
          <PaperWebcamModal
            view={gameView}
            onDispatch={dispatchIntent}
            onSendWebRtcSignal={sendWebRtcSignal}
            onInspectCardName={async (cardName) => {
              const res = await playtableApi.searchCards(cardName, false).catch(() => ({ cards: [] }));
              const top = res.cards[0];
              if (top) {
                setInspectedCard({
                  instanceId: `oracle_preview_${top.scryfallId}`,
                  ownerSeatId: mySeat?.seatId ?? 'seat_1',
                  controllerSeatId: mySeat?.seatId ?? 'seat_1',
                  zone: 'battlefield',
                  tapped: false,
                  faceDown: false,
                  flipped: false,
                  counters: {},
                  attachedToId: null,
                  exiledByInstanceId: null,
                  row: 'other',
                  freePos: null,
                  isToken: false,
                  isCommander: false,
                  enteredZoneSeq: 0,
                  card: top,
                });
              }
            }}
            onClose={() => setPaperModalOpen(false)}
          />
        )}

        {setupModalOpen && (
          <SetupAndLogModal
            view={gameView}
            overlaySecret={overlaySecret}
            reducedMotion={reducedMotion}
            onToggleReducedMotion={setReducedMotion}
            onDispatch={dispatchIntent}
            onHostAction={sendHostAction}
            onClose={() => setSetupModalOpen(false)}
          />
        )}

        {podModalOpen && (
          <PregameRuleZeroAndPodModal
            view={gameView}
            onDispatch={dispatchIntent}
            onClose={() => setPodModalOpen(false)}
          />
        )}

        {grantLibModalOpen && (
          <GrantLibraryAccessModal
            view={gameView}
            onDispatch={dispatchIntent}
            onClose={() => setGrantLibModalOpen(false)}
          />
        )}

        {helpersModalTab !== null && (
          <PlaytestHelpersAndCountersModal
            view={gameView}
            initialTab={helpersModalTab}
            onDispatch={dispatchIntent}
            onClose={() => setHelpersModalTab(null)}
          />
        )}

        {gameView.grantedLibraryViews.length > 0 && (
          <GrantedLibraryViewerModal
            grantView={gameView.grantedLibraryViews[0]}
            onDispatch={dispatchIntent}
            onInspect={(c) => setInspectedCard(c)}
          />
        )}

        {reportModalOpen && (
          <PostGameReportModal
            view={gameView}
            onDispatch={dispatchIntent}
            onOpenReplay={() => {
              setReportModalOpen(false);
              setReplayTableId(gameView.tableId);
            }}
            onClose={() => setReportModalOpen(false)}
          />
        )}

        {replayTableId && (
          <ReplayViewerModal
            tableId={replayTableId}
            onInspectCard={(c) => setInspectedCard(c)}
            onClose={() => setReplayTableId(null)}
          />
        )}
      </div>
    );
  }

  // ==========================================================================
  // LOBBY HOME SCREEN (CREATE GAME / SOLO GOLDFISH / JOIN BY CODE)
  // ==========================================================================
  return (
    <div
      className={`${
        embeddedInDeckstr
          ? 'min-h-full rounded-2xl border border-purple-500/20 bg-[#070a12]/95'
          : 'min-h-screen bg-[#070a12]'
      } text-slate-100 flex flex-col justify-between`}
    >
      <header className="border-b border-slate-800/80 bg-slate-950/80 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center text-slate-950 font-black shadow">
            P
          </div>
          <div>
            <div className="font-black tracking-tight text-sm leading-none">
              {embeddedInDeckstr ? 'DECKSTR SESSION · PLAYTABLE' : 'PLAYTABLE'}
            </div>
            <div className="text-[10px] text-slate-400">
              Authoritative Online MTG Sandbox · 1–8 Seats · Webcam & Digital Twin
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {onSwitchToLocalCouch && (
            <button
              type="button"
              onClick={onSwitchToLocalCouch}
              className="px-3 py-1.5 rounded-xl bg-purple-600/20 hover:bg-purple-600/35 border border-purple-400/50 text-purple-200 text-xs font-bold flex items-center gap-1.5"
              title="Switch to single-device local pass-and-play couch Life Tracker"
            >
              <Users className="w-3.5 h-3.5 text-purple-300" />
              <span>Local Couch Pass-and-Play</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              setCartModalDeck(sharedDeckPreview?.deck ?? deckstrDeck ?? null);
              setCartModalOpen(true);
            }}
            className="px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-emerald-500/40 text-emerald-200 text-xs font-bold flex items-center gap-1.5"
          >
            <ShoppingCart className="w-3.5 h-3.5 text-emerald-400" />
            <span>Buy Deck</span>
          </button>

          <button
            type="button"
            onClick={() => setCalendarModalOpen(true)}
            className="px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-rose-500/40 text-rose-200 text-xs font-bold flex items-center gap-1.5"
          >
            <Calendar className="w-3.5 h-3.5 text-rose-400" />
            <span>Streamed Calendar</span>
          </button>

          <button
            type="button"
            onClick={() => setLeaderboardModalOpen(true)}
            className="px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-amber-500/40 text-amber-200 text-xs font-bold flex items-center gap-1.5"
          >
            <Award className="w-3.5 h-3.5 text-amber-400" />
            <span>90-Day Rankings</span>
          </button>

          <button
            type="button"
            onClick={() => setAuthModalOpen(true)}
            className="px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-xs font-semibold flex items-center gap-2"
          >
            <User className="w-3.5 h-3.5 text-amber-400" />
            <span>{profile ? `${profile.displayName} (${profile.linkedProviders.length} linked)` : 'Sign In / Link Account'}</span>
          </button>
        </div>
      </header>

      <main className="max-w-5xl w-full mx-auto px-4 py-8 space-y-6">
        {deckstrDeck && (
          <div className="rounded-2xl bg-gradient-to-r from-amber-950/70 via-slate-950 to-purple-950/70 border border-amber-500/45 p-4 flex flex-wrap items-center justify-between gap-3 shadow-xl">
            <div className="space-y-0.5">
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-amber-300">
                Active Deckstr Deck Ready
              </div>
              <div className="text-sm font-extrabold text-white">{deckstrDeck.name}</div>
              <div className="text-xs text-slate-300">
                {deckstrDeck.commanders.length > 0 && (
                  <span className="text-amber-300 font-semibold mr-2">
                    Commander: {deckstrDeck.commanders.map((c) => c.name).join(' + ')}
                  </span>
                )}
                <span>
                  {deckstrDeck.mainboard.reduce((s, e) => s + e.quantity, 0)} mainboard ·{' '}
                  {deckstrDeck.sideboard.reduce((s, e) => s + e.quantity, 0)} sideboard
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => handleStartGoldfishWithDeck(deckstrDeck)}
                className="px-3.5 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs flex items-center gap-1.5 shadow"
              >
                <Play className="w-3.5 h-3.5" />
                <span>Goldfish This Deck</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setCartModalDeck(deckstrDeck);
                  setCartModalOpen(true);
                }}
                className="px-3 py-2 rounded-xl bg-emerald-600/25 hover:bg-emerald-600/40 border border-emerald-400/50 text-emerald-100 font-bold text-xs flex items-center gap-1.5"
              >
                <ShoppingCart className="w-3.5 h-3.5 text-emerald-300" />
                <span>Cheapest Cart</span>
              </button>
            </div>
          </div>
        )}

        {sharedDeckPreview && (
          <div className="rounded-2xl bg-gradient-to-r from-indigo-950/90 via-slate-950 to-violet-950/80 border border-indigo-500/50 p-4 flex flex-wrap items-center justify-between gap-3 shadow-2xl">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-400/50 text-indigo-200 text-[10px] font-extrabold uppercase tracking-wider">
                  Shared Deck Link
                </span>
                <span className="text-[11px] text-slate-400">
                  {FORMAT_PRESETS[sharedDeckPreview.format]?.label ?? sharedDeckPreview.format} · Era {sharedDeckPreview.eraNumber}
                </span>
              </div>
              <div className="text-sm font-extrabold text-white">{sharedDeckPreview.name}</div>
              <div className="text-xs text-slate-300">
                {sharedDeckPreview.deck.commanders.length > 0 && (
                  <span className="text-amber-300 font-semibold mr-2">
                    Commander: {sharedDeckPreview.deck.commanders.map((c) => c.name).join(' + ')}
                  </span>
                )}
                <span>
                  {sharedDeckPreview.deck.mainboard.reduce((s, e) => s + e.quantity, 0)} mainboard ·{' '}
                  {sharedDeckPreview.deck.sideboard.reduce((s, e) => s + e.quantity, 0)} sideboard
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {sharedDeckCopiedMsg ? (
                <span className="px-3 py-1.5 rounded-xl bg-emerald-500/20 border border-emerald-400/60 text-emerald-200 text-xs font-bold">
                  {sharedDeckCopiedMsg}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const acct = await ensureSignedInQuick();
                      if (!acct) return;
                      const res = await playtableApi.copySharedDeckToLibrary(sharedDeckPreview.shareSlug);
                      setSharedDeckCopiedMsg(`Saved "${res.savedDeck.name}" to your library!`);
                    } catch (err: any) {
                      setToastError(err.message);
                    }
                  }}
                  className="px-3.5 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs shadow"
                >
                  Copy this deck into my library
                </button>
              )}
              <button
                type="button"
                onClick={() => setSharedDeckPreview(null)}
                className="px-2.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 text-xs"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-6 items-start">
        {/* Left: Create Game Card (Private/Public, Format, Seat Limit 1..8, Toggles) */}
        <form
          onSubmit={handleCreateTable}
          className="rounded-2xl bg-[#101625] border border-slate-800/90 p-5 space-y-4 shadow-2xl"
        >
          <div className="flex items-center justify-between">
            <h1 className="text-base font-extrabold">Create Table</h1>
            <button
              type="button"
              onClick={handleStartGoldfish}
              className="px-3 py-1.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/60 text-amber-200 font-bold text-xs flex items-center gap-1.5"
            >
              <Play className="w-3.5 h-3.5" />
              <span>1-Tap Solo Goldfish</span>
            </button>
          </div>

          {/* Visibility Segmented Control */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-400 mb-1.5">Visibility</label>
            <div className="grid grid-cols-2 p-1 rounded-xl bg-slate-950 border border-slate-800">
              <button
                type="button"
                onClick={() => setCreateVisibility('private')}
                className={`py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-colors ${
                  createVisibility === 'private'
                    ? 'bg-violet-600 text-white shadow'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Lock className="w-3.5 h-3.5" />
                <span>Private</span>
              </button>
              <button
                type="button"
                onClick={() => setCreateVisibility('public')}
                className={`py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-colors ${
                  createVisibility === 'public'
                    ? 'bg-violet-600 text-white shadow'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Unlock className="w-3.5 h-3.5" />
                <span>Public</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div>
              <label className="block text-slate-400 font-semibold mb-1">Table Name</label>
              <input
                required
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white"
              />
            </div>

            <div>
              <label className="block text-slate-400 font-semibold mb-1">Format Preset</label>
              <select
                value={createFormat}
                onChange={(e) => {
                  const fmt = e.target.value as FormatId;
                  setCreateFormat(fmt);
                  setCreateMaxSeats(FORMAT_PRESETS[fmt].defaultSeats);
                }}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white"
              >
                {Object.values(FORMAT_PRESETS).map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.label} ({preset.startingLife} life)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-slate-400 font-semibold mb-1">Seat Limit (1 Goldfish – 8 Pod)</label>
              <select
                value={createMaxSeats}
                onChange={(e) => setCreateMaxSeats(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n}>
                    {n === 1 ? '1 (Solo Goldfish)' : `${n} Players`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-slate-400 font-semibold mb-1">Bracket (optional)</label>
              <select
                value={createBracket}
                onChange={(e) => setCreateBracket(e.target.value as '' | '1' | '2' | '3' | '4' | '5')}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white"
              >
                <option value="">Casual / No bracket</option>
                <option value="1">Bracket 1 — Exhibition</option>
                <option value="2">Bracket 2 — Core / Precon</option>
                <option value="3">Bracket 3 — Upgraded</option>
                <option value="4">Bracket 4 — Optimized</option>
                <option value="5">Bracket 5 — cEDH</option>
              </select>
            </div>

            <div>
              <label className="block text-slate-400 font-semibold mb-1">Language</label>
              <select
                value={createLanguage}
                onChange={(e) => setCreateLanguage(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white"
              >
                {['English', 'Español', 'Français', 'Deutsch', '日本語', 'Português'].map((lang) => (
                  <option key={lang} value={lang}>
                    {lang}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-slate-400 font-semibold mb-1">Password (optional)</label>
              <input
                type="password"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                placeholder="Secret code..."
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-slate-400 font-semibold mb-1">Description (optional)</label>
              <textarea
                rows={2}
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                placeholder="Enter game details, house rules, Discord info, etc."
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white"
              />
            </div>

            <div className="sm:col-span-2 grid grid-cols-2 gap-2">
              <label className="flex items-center justify-between px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 cursor-pointer">
                <span className="text-slate-300">Spectators</span>
                <input
                  type="checkbox"
                  checked={createSpectators}
                  onChange={(e) => setCreateSpectators(e.target.checked)}
                  className="accent-violet-500"
                />
              </label>
              <label className="flex items-center justify-between px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 cursor-pointer">
                <span className="text-slate-300">Late join requests</span>
                <input
                  type="checkbox"
                  checked={createLateJoin}
                  onChange={(e) => setCreateLateJoin(e.target.checked)}
                  className="accent-violet-500"
                />
              </label>
              <label className="flex items-center justify-between px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 cursor-pointer">
                <span className="text-slate-300">Allow Spectator Chat</span>
                <input
                  type="checkbox"
                  checked={createSpectatorChat}
                  onChange={(e) => setCreateSpectatorChat(e.target.checked)}
                  className="accent-violet-500"
                />
              </label>
              <label className="flex items-center justify-between px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 cursor-pointer">
                <span className="text-slate-300">Competitive mode</span>
                <input
                  type="checkbox"
                  checked={createCompetitive}
                  onChange={(e) => setCreateCompetitive(e.target.checked)}
                  className="accent-violet-500"
                />
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="w-full py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-extrabold text-sm flex items-center justify-center gap-2 shadow-lg"
          >
            <Swords className="w-4 h-4" />
            <span>Create Game</span>
          </button>
        </form>

        {/* Right: Play Now Matchmaking Queue / Join by Code / Saved Pods / Public Lobbies */}
        <div className="space-y-4">
          <PlayNowQueuePanel
            ensureSignedIn={ensureSignedInQuick}
            onMatchedPod={(matchedId) => {
              setSessionMode('table');
              setActiveTableId(matchedId);
            }}
            onToastError={(msg) => setToastError(msg)}
          />

          <div className="rounded-2xl bg-[#101625] border border-slate-800/90 p-5 space-y-3 shadow-xl">
            <h2 className="text-sm font-extrabold">Join by Invite Code</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                value={inviteCodeInput}
                onChange={(e) => setInviteCodeInput(e.target.value.toUpperCase())}
                placeholder="6-CHAR CODE (e.g. K7M9P2)"
                maxLength={8}
                className="px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 font-mono uppercase tracking-widest text-center text-xs"
              />
              <input
                type="password"
                value={joinPasswordInput}
                onChange={(e) => setJoinPasswordInput(e.target.value)}
                placeholder="Password (if protected)"
                className="px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <button
                type="button"
                disabled={!inviteCodeInput.trim()}
                onClick={() => handleJoinByCode(true)}
                className="py-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 font-bold disabled:opacity-40"
              >
                Join as Spectator
              </button>
              <button
                type="button"
                disabled={!inviteCodeInput.trim()}
                onClick={() => handleJoinByCode(false)}
                className="py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold disabled:opacity-40"
              >
                Take a Seat
              </button>
            </div>
          </div>

          {resumableGames.length > 0 && (
            <div className="rounded-2xl bg-[#101625] border border-emerald-500/40 p-4 space-y-2 shadow-xl">
              <div className="text-xs font-extrabold text-emerald-300">Resume Saved / In-Progress Pods</div>
              {resumableGames.map((g) => (
                <div
                  key={g.tableId}
                  className="p-2 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between text-xs"
                >
                  <div>
                    <div className="font-bold text-white">{g.name}</div>
                    <div className="text-[10px] text-slate-400">
                      {FORMAT_PRESETS[g.format]?.label} · Turn {g.turnNumber} · {g.status.toUpperCase()}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSessionMode('table');
                      setActiveTableId(g.tableId);
                    }}
                    className="px-3 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold"
                  >
                    Resume
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-2xl bg-[#101625] border border-slate-800/90 p-5 space-y-3 shadow-xl">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-extrabold flex items-center gap-1.5">
                <Users className="w-4 h-4 text-violet-400" />
                <span>Open Public Tables</span>
              </h2>
              <button
                type="button"
                onClick={async () => {
                  const pub = await playtableApi.listPublicTables({
                    format: lobbyFilterFormat,
                    seatsOpen: lobbyFilterOpenOnly,
                    search: lobbyFilterSearch,
                  });
                  setPublicTables(pub.tables);
                }}
                className="text-[11px] text-slate-400 hover:text-white"
              >
                Refresh
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-[11px]">
              <select
                value={lobbyFilterFormat}
                onChange={async (e) => {
                  const fmt = e.target.value as FormatId | '';
                  setLobbyFilterFormat(fmt);
                  const pub = await playtableApi.listPublicTables({
                    format: fmt,
                    seatsOpen: lobbyFilterOpenOnly,
                    search: lobbyFilterSearch,
                  });
                  setPublicTables(pub.tables);
                }}
                className="px-2 py-1 rounded-lg bg-slate-950 border border-slate-800 text-slate-200"
              >
                <option value="">All Formats</option>
                {Object.values(FORMAT_PRESETS).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>

              <input
                value={lobbyFilterSearch}
                onChange={async (e) => {
                  const val = e.target.value;
                  setLobbyFilterSearch(val);
                  const pub = await playtableApi.listPublicTables({
                    format: lobbyFilterFormat,
                    seatsOpen: lobbyFilterOpenOnly,
                    search: val,
                  });
                  setPublicTables(pub.tables);
                }}
                placeholder="Filter name/rules..."
                className="px-2 py-1 rounded-lg bg-slate-950 border border-slate-800 text-slate-200"
              />

              <label className="flex items-center justify-between px-2 py-1 rounded-lg bg-slate-950 border border-slate-800 cursor-pointer">
                <span className="text-slate-300">Open seats</span>
                <input
                  type="checkbox"
                  checked={lobbyFilterOpenOnly}
                  onChange={async (e) => {
                    const openOnly = e.target.checked;
                    setLobbyFilterOpenOnly(openOnly);
                    const pub = await playtableApi.listPublicTables({
                      format: lobbyFilterFormat,
                      seatsOpen: openOnly,
                      search: lobbyFilterSearch,
                    });
                    setPublicTables(pub.tables);
                  }}
                  className="accent-violet-500"
                />
              </label>
            </div>

            {publicTables.length === 0 ? (
              <p className="text-xs text-slate-500 py-4 text-center">
                No matching public tables right now — create one, queue in Play Now, or jump into Solo Goldfish!
              </p>
            ) : (
              <div className="space-y-2">
                {publicTables.map((t) => (
                  <div
                    key={t.tableId}
                    className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between gap-2 text-xs"
                  >
                    <div className="min-w-0">
                      <div className="font-bold text-slate-100 flex items-center gap-1.5">
                        <span className="truncate">{t.name}</span>
                        {t.hasPassword && <Lock className="w-3 h-3 text-amber-400 shrink-0" />}
                        {t.bracket && (
                          <span className="px-1.5 py-0.2 rounded bg-violet-500/20 text-violet-200 text-[9px]">
                            B{t.bracket}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {FORMAT_PRESETS[t.format]?.label} · {t.seatsFilled}/{t.maxSeats} seats · {t.language ?? 'English'}
                      </div>
                      {t.description && (
                        <div className="text-[10px] text-slate-500 truncate">{t.description}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const acct = await ensureSignedInQuick();
                          if (!acct) return;
                          const joined = await playtableApi.joinTable({
                            tableId: t.tableId,
                            password: joinPasswordInput.trim() || undefined,
                          });
                          setSessionMode(joined.sessionType);
                          setActiveTableId(joined.tableId);
                        } catch (err: any) {
                          setToastError(err.message);
                        }
                      }}
                      className="px-3 py-1 rounded-lg bg-violet-600 hover:bg-violet-500 font-bold text-white shrink-0"
                    >
                      Join
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>
      </main>

      {toastError && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl bg-rose-950 border border-rose-500 text-rose-200 text-xs shadow-2xl z-50">
          {toastError}
        </div>
      )}

      {/* Wizards Fan Content Policy & Scryfall Disclaimer */}
      <footer className="border-t border-slate-900 px-4 py-3 text-center text-[10px] text-slate-500 leading-relaxed">
        Playtable is unofficial Fan Content permitted under the Wizards of the Coast Fan Content Policy. Not approved/endorsed by Wizards. Portions of the materials used are property of Wizards of the Coast. ©Wizards of the Coast LLC. Card data and images courtesy of Scryfall. Age 13+ required.
      </footer>

      {authModalOpen && (
        <AuthAccountModal
          profile={profile}
          devAuthEnabled={devAuthEnabled}
          onProfileChanged={(p) => setProfile(p)}
          onSignedOut={() => setProfile(null)}
          onClose={() => setAuthModalOpen(false)}
        />
      )}

      {leaderboardModalOpen && (
        <FormatLeaderboardModal
          profile={profile}
          onProfileChanged={(p) => setProfile(p)}
          onClose={() => setLeaderboardModalOpen(false)}
        />
      )}

      {calendarModalOpen && (
        <StreamedCalendarModal
          profile={profile}
          ensureSignedIn={ensureSignedInQuick}
          onJoinLaunchedTable={(tid, secret) => {
            setCalendarModalOpen(false);
            if (secret) setOverlaySecret(secret);
            setSessionMode('table');
            setActiveTableId(tid);
          }}
          onClose={() => setCalendarModalOpen(false)}
        />
      )}

      {cartModalOpen && (
        <DeckCartModal
          initialDeck={cartModalDeck}
          profile={profile}
          ensureSignedIn={ensureSignedInQuick}
          onClose={() => setCartModalOpen(false)}
        />
      )}
    </div>
  );
}
